import React from 'react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Calculator } from 'lucide-react';
import { findiriscQuestions } from '../lib/findrisc-questions';
import { BmiCalculator } from './BmiCalculator';
import type { FindriscAnswers } from '../types/findrisc';

interface QuestionCardProps {
  step: number;
  answers: FindriscAnswers;
  onAnswer: (questionId: string, value: any) => void;
  onNext: () => void;
  onPrev: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  totalQuestions: number;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  step,
  answers,
  onAnswer,
  onNext,
  onPrev,
  canGoNext,
  canGoPrev,
  totalQuestions
}) => {
  const currentQuestion = findiriscQuestions[step];
  
  // Skip waist circumference questions based on sex
  const getVisibleQuestion = () => {
    if (step === 3) {
      // Waist circumference - show appropriate question based on sex
      if (answers.sex === 'male') {
        return findiriscQuestions[3];
      } else if (answers.sex === 'female') {
        return findiriscQuestions[4];
      } else {
        return findiriscQuestions[3]; // Default to male until sex is selected
      }
    }
    
    // Skip the female waist question when displaying
    if (step > 3) {
      return findiriscQuestions[step + 1];
    }
    
    return currentQuestion;
  };

  const question = getVisibleQuestion();
  const currentValue = (answers as any)[question.id];

  const handleAnswer = (value: any) => {
    onAnswer(question.id, value);
  };

  const isAnswered = currentValue !== undefined && currentValue !== null;

  const renderQuestion = () => {
    switch (question.type) {
      case 'choice':
        return (
          <RadioGroup
            value={currentValue || ''}
            onValueChange={handleAnswer}
            className="space-y-3"
          >
            {question.options?.map((option) => (
              <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-secondary/50 transition-colors">
                <RadioGroupItem value={option.value} id={option.value} />
                <Label htmlFor={option.value} className="flex-1 cursor-pointer text-sm font-medium">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'boolean':
        return (
          <RadioGroup
            value={currentValue || ''}
            onValueChange={handleAnswer}
            className="space-y-3"
          >
            {question.options?.map((option) => (
              <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-secondary/50 transition-colors">
                <RadioGroupItem value={option.value} id={option.value} />
                <Label htmlFor={option.value} className="flex-1 cursor-pointer text-sm font-medium">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'bmi-calculator':
        return (
          <BmiCalculator
            answers={answers}
            onAnswer={onAnswer}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {question.title}
          </h2>
          {question.description && (
            <p className="text-muted-foreground text-sm">
              {question.description}
            </p>
          )}
        </div>

        <Card className="bg-secondary/20">
          <CardContent className="p-4">
            {renderQuestion()}
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={onPrev}
          disabled={!canGoPrev}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        <Button
          onClick={onNext}
          disabled={!isAnswered}
          className="flex items-center gap-2 bg-gradient-primary"
        >
          {step === totalQuestions - 1 ? 'Finish' : 'Next'}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};